import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client.js";
import type { FindingPage } from "../../api/types.js";
import { InlineError } from "../../components/AsyncState.js";
import { SeverityBadge, StatusBadge } from "../../components/Badge.js";
import { formatInteger } from "../../lib/format.js";
import { displayRuleId } from "../../app/rule-id.js";
import { affectedFindingHref, affectedFindingsQuery } from "./inventory-affected-findings.js";
import type { InventorySelection } from "./inventory-selection.js";
import "./inventory-affected-findings.css";

export const AFFECTED_FINDINGS_HONESTY = "No linked finding is not a clean advisory state.";
export const UNLINKED_INVENTORY_FINDING_COPY =
  "No vulnerability finding is linked to this inventory observation.";

export function InventoryAffectedFindings({ selection }: { selection: InventorySelection }) {
  const query = affectedFindingsQuery(selection);
  const [page, setPage] = useState<FindingPage | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(query !== null);
  const requestRef = useRef<AbortController | null>(null);

  const refresh = useCallback(() => {
    const nextQuery = affectedFindingsQuery(selection);
    requestRef.current?.abort();
    if (!nextQuery) {
      setPage(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    requestRef.current = controller;
    setPage(null);
    setError(null);
    setIsLoading(true);
    void api
      .getFindings(nextQuery, controller.signal)
      .then((nextPage) => {
        if (controller.signal.aborted) return;
        setPage(nextPage);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          reason instanceof Error ? reason : new Error("The request could not be completed"),
        );
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setIsLoading(false);
      });
  }, [selection]);

  useEffect(() => {
    refresh();
    return () => {
      requestRef.current?.abort();
    };
  }, [refresh]);

  return (
    <section
      className="inventory-affected-findings"
      aria-labelledby="inventory-affected-findings-heading"
    >
      <h3 id="inventory-affected-findings-heading">Affected findings</h3>
      {!query ? (
        <p className="inventory-affected-findings__unlinked">{UNLINKED_INVENTORY_FINDING_COPY}</p>
      ) : isLoading ? (
        <p className="inventory-affected-findings__status" role="status">
          Loading affected findings
        </p>
      ) : error ? (
        <InlineError error={error} onRetry={refresh} />
      ) : (
        <>
          <p className="inventory-affected-findings__count">
            {formatInteger(page?.total ?? 0)} matching on this scan
          </p>
          {page?.items.length ? (
            <ul className="inventory-affected-findings__list">
              {page.items.map((finding) => {
                const href = affectedFindingHref(selection, finding.id);
                return (
                  <li key={finding.id}>
                    <a className="inventory-affected-findings__item" href={href ?? undefined}>
                      <span className="inventory-affected-findings__severity">
                        <SeverityBadge severity={finding.severity} />
                      </span>
                      <span className="inventory-affected-findings__title">{finding.title}</span>
                      <span className="inventory-affected-findings__status-badge">
                        <StatusBadge status={finding.status} />
                      </span>
                      <span className="inventory-affected-findings__meta">
                        <code>
                          {finding.location.path}:{finding.location.lineStart}
                        </code>
                        <code>{displayRuleId(finding.ruleId)}</code>
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="inventory-affected-findings__empty">
              <strong>No advisory finding is linked to this exact identity.</strong>
              <p>
                This means the configured advisory engines did not emit a match for
                {selection.kind === "sbom"
                  ? ` ${selection.row.name}@${selection.row.version || "unresolved"} in ${selection.row.manifestPath}`
                  : " this cryptographic observation"}
                . It does not prove the component is safe, unreachable, or absent from production.
              </p>
              {selection.kind === "sbom" ? (
                <p>
                  Verify advisory-database freshness, confirm runtime import or image presence, and
                  rescan the regenerated lockfile before closing the review.
                </p>
              ) : null}
            </div>
          )}
        </>
      )}
      <p className="inventory-affected-findings__honesty">{AFFECTED_FINDINGS_HONESTY}</p>
    </section>
  );
}
